import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  isPromotableDraft,
  writebackApi,
  type WritebackInvoiceKind,
  type WritebackPromotionResult,
} from '../../api/writeback';
import { useIsMirror } from '../../hooks/useIsMirror';

/**
 * ADR-010/012 — "Registruoti kliento knygoje": promotes an unposted a5suite
 * draft (id >= 1_000_000_000) into the client's real Apskaita5 book. Mirror
 * workspaces are read-only (ADR-004) EXCEPT this one sanctioned command.
 * kind 'journal' (ADR-010), 'made' / 'received' (ADR-012 invoice lanes —
 * an Unknown outcome resolves by REPLAYING the command, there is no probe).
 *
 * One click per decision: no confirmation dialog. The inline control reflects
 * the promotion state machine; outcome copy comes from the API verbatim — the
 * frontend never authors error text for API failures. After Confirmed/Reconciled
 * the chip replaces the button for good (the status query is the persistent
 * source of truth, so it survives reloads).
 */
export default function PromoteToClientBook({
  entry,
  kind = 'journal',
}: {
  entry: { id: number; hasBeenPosted?: boolean; isPosted?: boolean };
  kind?: 'journal' | WritebackInvoiceKind;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMirror = useIsMirror();

  const queryKey = ['promotion', kind, entry.id];
  const { data: promotion } = useQuery({
    queryKey,
    queryFn: () =>
      kind === 'journal'
        ? writebackApi.getPromotion(entry.id)
        : writebackApi.getInvoicePromotion(kind, entry.id),
    enabled: isMirror && isPromotableDraft(entry),
  });

  const promote = useMutation({
    mutationFn: () =>
      kind === 'journal'
        ? writebackApi.promote(entry.id)
        : writebackApi.promoteInvoice(kind, entry.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
  const check = useMutation({
    mutationFn: () =>
      kind === 'journal'
        ? writebackApi.checkOutcome(entry.id)
        : writebackApi.checkInvoiceOutcome(kind, entry.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (!isMirror || !isPromotableDraft(entry)) return null;

  // Latest action result wins (immediate feedback); the status query covers reloads.
  const result: WritebackPromotionResult | undefined = check.data ?? promote.data ?? promotion ?? undefined;
  const isSuccess = result?.state === 'Confirmed' || result?.state === 'Reconciled';
  const actionInFlight = promote.isPending || check.isPending;
  // Unknown, or a stale server Pending (send died mid-flight) with no action
  // running locally — both resolve through the same user action again (the JE
  // lane probes the ledger, the invoice lanes replay the command).
  const needsCheck =
    result?.state === 'Unknown' ||
    (!actionInFlight && (promotion?.state === 'Unknown' || promotion?.state === 'Pending'));

  if (isSuccess) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        {result!.legacyEntryId
          ? t('writeback.promotionDoneNumber', { id: result!.legacyEntryId })
          : t('writeback.promotionDone')}
      </span>
    );
  }

  const isUnknown = result?.state === 'Unknown';

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
        disabled={actionInFlight}
        onClick={() => (needsCheck ? check.mutate() : promote.mutate())}
      >
        {actionInFlight
          ? t('writeback.promotionSending')
          : needsCheck
            ? t('writeback.promotionCheck')
            : result?.state === 'Failed'
              ? t('writeback.promotionRetry')
              : t('writeback.promoteToClientBook')}
      </button>
      {result && result.message && (
        <div className={`text-xs max-w-xs text-right ${isUnknown ? 'text-amber-600' : 'text-red-600'}`}>
          {result.message}
        </div>
      )}
    </div>
  );
}
